import React from "react"
import Relay from "react-relay"
import Dropzone from "react-dropzone"
import Video from "react-html5video"
import classnames from "classnames"
import RadioGroup from "react-radio-group"
import update from "react-addons-update"
import { browserHistory } from "react-router"
import PropTypes from "prop-types"
import { Line } from "rc-progress"
import { keyBy } from "lodash/collection"
import { camelCase, upperFirst } from "lodash/string"
import { injectIntl, FormattedMessage, FormattedHTMLMessage } from "react-intl"
import sharedMessages from "../../messages/index"
import mediaMessages from "../../messages/media"
import settingsMessages from "../../messages/settings"
import { messages as licenseMessages } from "../../messages/licenses"
import { licenseTypes } from "../../scripts/licenseTypes"
import { userAgent, isKeyClick } from "../../scripts/utils"
import { saveMedia } from "../../scripts/backend"
import Icon from "../elements/Icon"
import RichTextEditor from "../elements/RichTextEditor"
import AudioPlayer from "../elements/AudioPlayer"
import InlineResource from "./InlineResource"
import Image from "../elements/Image"
import ReactSVG from "react-svg"
import { getAccountSettings } from "../../scripts/accountSettings"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"
import moment from "moment"

class MediaUpload extends React.Component {
  constructor(props) {
    super(props)

    const { description, license, date, tags, coverage } = props.resource ? JSON.parse(props.resource.custom_fields) : {}

    this.state = {
      showFileUpload: !props.resource,
      files: [],
      id: props.resource && props.resource.id,
      fields: props.resource
        ? {
            title: props.resource.title,
            description: description ? description.value : "",
            license: license ? license.value : "",
            date: date ? date.value : "",
            coverage: coverage ? coverage.value : "",
            location: props.resource.location,
            tags: tags ? tags.value.join(", ") : "",
            creator: props.resource.creator
          }
        : {},
      saving: false,
      saved: false,
      uploadPercent: 0,
      uploadedType: props.resource ? props.resource.type : null, // will be set after upload to image | video | audio
      errors: {}
    }

    const { type = "", intl } = props

    this._requiredFields = [
      {
        name: "title",
        error: intl.formatMessage(sharedMessages.enterTitle, { type })
      },
      {
        name: "description",
        error: intl.formatMessage(sharedMessages.enterDescription, { type })
      },
      {
        name: "files",
        error: intl.formatMessage(sharedMessages.noImage, { type })
      },
      {
        name: "license",
        error: intl.formatMessage(sharedMessages.noLicense, { type })
      }
    ]
  }

  getContributionLicenses = Radio => {
    const { intl } = this.props
    const {
      settings: {
        contribution_licenses: contributionLicenses = [],
        contact_email: contactEmail
      }
    } = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )

    return contributionLicenses.map((item, i) => {
      if (item === "no_known_restrictions") {
        return undefined
      }

      const license = licenseTypes.find(type => type.name === item)

      const label =
        license && license.label in licenseMessages
          ? intl.formatMessage(licenseMessages[license.label], {
              email: contactEmail
            })
          : ""

      return (
        <label key={i}>
          <Radio value={item} />
          {label}
        </label>
      )
    })
  }

  render() {
    const {
      files,
      fields,
      errors,
      showFileUpload,
      saving,
      uploadPercent
    } = this.state
    const { type, resource, intl } = this.props
    const accept = type === "media" ? "audio/*,image/*,video/*" : type + "/*"
    const browserTiffSupport = userAgent().isSafari
    const typeMessageKey = `type${upperFirst(camelCase(type))}`
    const typeMessage =
      typeMessageKey in sharedMessages
        ? intl.formatMessage(sharedMessages[typeMessageKey])
        : type
    const {
      settings: {
        style: { content: contentType }
      }
    } = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )

    return (
      <div
        className={classnames("c-image-upload", {
          "state-image-upload-saving": this.state.saving,
          "state-image-upload-saved": this.state.saved,
          "state-image-upload-invalid": errors.length > 0
        })}
      >
        {getStyleTag([
          "c-article__media",
          "form-message",
          "editable-item",
          "editable-item__actions__button"
        ])}

        <form onSubmit={this._handleSubmit}>
          {(showFileUpload || resource) && (
            <div className="c-image-upload__image-area">
              {files.length < 1 &&
                !resource && (
                  <div>
                    {errors.files && (
                      <span className="form-message state-error">
                        {errors.files.error}
                      </span>
                    )}
                    {getStyleTag("c-image-upload__dropzone")}
                    <Dropzone
                      multiple={false}
                      accept={accept}
                      onDrop={this._onDrop}
                      className={classnames("c-image-upload__dropzone", {
                        "state-invalid": errors.files
                      })}
                      activeClassName="c-image-upload__dropzone state-dropzone-active"
                    >
                      <div className="media-object">
                        <div className="media-object-section">
                          {contentType == "type1" ? (
                            <ReactSVG src="../../images/cloud-computing.svg" />
                          ) : (
                            <Icon
                              icon="upload-circle"
                              extraClasses="icon--2x"
                            />
                          )}
                        </div>
                        <div className="media-object-section">
                          <FormattedMessage
                            {...mediaMessages.dragFile}
                            values={{ type: typeMessage.toLowerCase() }}
                          />
                        </div>
                      </div>
                    </Dropzone>
                    <p
                      className={classnames(
                        "mtt small line-tight",
                        errors.dropzone && "form-message state-error"
                      )}
                    >
                      <em style={getStyles("c-article__media-title")}>
                        <FormattedMessage
                          {...mediaMessages.acceptedFiletypes}
                        />
                      </em>
                      {(type === "image" || type === "media") && (
                        <span>
                          <br />
                          <strong>
                            <FormattedMessage {...sharedMessages.typeImage} />{" "}
                            &ndash;
                          </strong>{" "}
                          jpg, jpeg, png, gif, bmp, tif, tiff
                        </span>
                      )}
                      {(type === "audio" || type === "media") && (
                        <span>
                          <br />
                          <strong>
                            <FormattedMessage {...sharedMessages.typeAudio} />{" "}
                            &ndash;
                          </strong>{" "}
                          aac, tiff, flac, m4a, mp3, ogg, wav
                        </span>
                      )}
                      {(type === "video" || type === "media") && (
                        <span>
                          <br />
                          <strong>
                            <FormattedMessage {...sharedMessages.typeVideo} />{" "}
                            &ndash;
                          </strong>{" "}
                          mpg, avi, mov, wmv, mp4, m4v, ogv, webm, mkv, flv
                        </span>
                      )}
                    </p>
                  </div>
                )}

              {files.length > 0 &&
                !resource && (
                  <div className="c-image-upload__attached-images">
                    {this.state.files.map((file, i) => (
                      <figure
                        key={i}
                        className={classnames("editable-item", {
                          "c-image-upload__attached-image":
                            file.type.indexOf("image") > -1,
                          figure__audio: file.type.indexOf("audio") > -1,
                          "figure__audio--smallform":
                            file.type.indexOf("audio") > -1
                        })}
                      >
                        {file.type.indexOf("image") > -1 && (
                          <span>
                            {/* if is NOT tiff file, or is tiff file but has browser support */}
                            {!(
                              file.type.indexOf("tiff") > -1 &&
                              !browserTiffSupport
                            ) && (
                              <Image src={file.preview} skipVersion={true} />
                            )}

                            {/* if is tiff file and has browser support */}
                            {file.type.indexOf("tiff") > -1 &&
                              !browserTiffSupport && (
                                <div className="c-image-upload__preview-unnavailable">
                                  <FormattedHTMLMessage
                                    {...mediaMessages.previewNotAvailable}
                                  />
                                </div>
                              )}
                          </span>
                        )}
                        {file.type.indexOf("video") > -1 && (
                          <Video controls>
                            <source src={file.preview} type={file.type} />
                          </Video>
                        )}
                        {file.type.indexOf("audio") > -1 && (
                          <AudioPlayer
                            smallForm={true}
                            src={file.preview}
                            className="c-mviewer__audio"
                          >
                            <audio style={{ display: "none" }} controls>
                              <source src={file.preview} type={file.type} />
                              <FormattedMessage
                                {...mediaMessages.audioNotSupported}
                              />
                            </audio>
                          </AudioPlayer>
                        )}
                        {this.props.showMediaClear && (
                          <div className="editable-item__actions">
                            <button
                              type="button"
                              style={getStyles([
                                "c-button__icon",
                                "c-button__col"
                              ])}
                              className={
                                contentType == "default" &&
                                "button editable-item__btn--alert"
                              }
                              onKeyPress={e =>
                                isKeyClick(e) ? this._handleMediaClear(e) : null
                              }
                              onClick={e => {
                                this._handleMediaClear(e)
                              }}
                              title={"Remove " + type}
                            >
                              <Icon icon="cross" />
                            </button>
                          </div>
                        )}
                      </figure>
                    ))}
                  </div>
                )}

              {!showFileUpload &&
                resource && (
                  <div className="c-image-upload__attached-images">
                    <figure className="c-image-upload__attached-image">
                      <InlineResource
                        title={resource.title}
                        resource={resource}
                      />
                    </figure>
                  </div>
                )}
            </div>
          )}

          {(files.length > 0 || resource) && (
            <div className="c-image-upload__meta">
              <label className="required" htmlFor="title">
                <FormattedMessage {...sharedMessages.formTitle} />
              </label>
              {errors.title && (
                <span className="form-message state-error">
                  {errors.title.error}
                </span>
              )}
              <input
                className={classnames({ "state-invalid": errors.title })}
                type="text"
                id="title"
                name="title"
                value={fields.title}
                onChange={this._changeValue}
              />

              <label className="required" htmlFor="description">
                <FormattedMessage {...sharedMessages.formDescription} />
              </label>
              {errors.description && (
                <span className="form-message state-error">
                  {errors.description.error}
                </span>
              )}

              <div className="mbs">
                <RichTextEditor
                  className={classnames({
                    "state-invalid": errors.description
                  })}
                  name="description"
                  value={fields.description}
                  onChange={value => this._changeField("description", value)}
                  id="description"
                  rows="2"
                />
              </div>

              <div className="row nested">
                <div className="small-12 medium-6 columns">
                  <label htmlFor="date">
                    <FormattedMessage {...sharedMessages.formDate} />
                  </label>
                  {errors.date && (
                    <span className="form-message state-error">
                      {errors.date.error}
                    </span>
                  )}
                  <input
                    aria-describedby="imageUploadDateHelp"
                    type="text"
                    id="date"
                    name="date"
                    value={fields.date}
                    onChange={this._changeValue}
                    placeholder="YYYY-MM-DD"
                  />
                  <p
                    className="help-text"
                    id="imageUploadDateHelp"
                    style={getStyles("help-text")}
                  >
                    <FormattedHTMLMessage
                      {...sharedMessages.edtfDateInstructions}
                    />
                  </p>
                </div>
                <div className="small-12 medium-6 columns">
                  <label htmlFor="coverage">
                    <FormattedMessage {...sharedMessages.formLocation} />
                  </label>
                  {errors.coverage && (
                    <span className="form-message state-error">
                      {errors.coverage.error}
                    </span>
                  )}
                  <input
                    type="text"
                    id="coverage"
                    name="coverage"
                    value={fields.coverage}
                    onChange={this._changeValue}
                  />
                </div>
              </div>

              <label htmlFor="tags">
                <FormattedMessage {...sharedMessages.formTags} />
              </label>
              {errors.tags && (
                <span className="form-message state-error">
                  {errors.tags.error}
                </span>
              )}
              <input
                aria-describedby="imageUploadTagHelp"
                type="text"
                id="tags"
                name="tags"
                value={fields.tags}
                onChange={this._changeValue}
              />
              <p
                className="help-text"
                id="imageUploadTagHelp"
                style={getStyles("help-text")}
              >
                <FormattedMessage {...sharedMessages.formTagsHelp} />
              </p>

              <label htmlFor="creator">
                <FormattedMessage {...sharedMessages.formCreator} />
              </label>
              {errors.creator && (
                <span className="form-message state-error">
                  {errors.creator.error}
                </span>
              )}
              <input
                aria-describedby="creatorHelp"
                className={classnames({ "state-invalid": errors.creator })}
                type="text"
                id="creator"
                name="creator"
                value={fields.creator}
                onChange={this._changeValue}
              />
              <p
                className="help-text"
                id="creatorHelp"
                style={getStyles("help-text")}
              >
                <FormattedMessage
                  {...sharedMessages.formCreatorHelp}
                  values={{ type: typeMessage.toLowerCase() }}
                />
              </p>

              <label className="required">
                <FormattedMessage {...sharedMessages.formLicense} />
              </label>
              {errors.license && (
                <span className="form-message state-error">
                  {errors.license.error}
                </span>
              )}
              <div className="form-group form-group--radio">
                <RadioGroup
                  name="license"
                  selectedValue={fields.license}
                  onChange={value =>
                    this.setState({
                      fields: update(this.state.fields, {
                        $merge: { license: value }
                      })
                    })
                  }
                >
                  {Radio => (
                    <div>
                      {this.getContributionLicenses(Radio)}
                      <label>
                        <Radio value="no_known_restrictions" />
                        <FormattedMessage
                          {...sharedMessages.formLicenseOutOfCopyright}
                          values={{ type: typeMessage.toLowerCase() }}
                        />
                      </label>
                    </div>
                  )}
                </RadioGroup>
              </div>
            </div>
          )}
          {(files.length > 0 || resource) && (
            <div className="form-actions" style={getStyles("form-actions")}>
              {errors.form && (
                <span className="form-message state-error">{errors.form}</span>
              )}
              {resource && (
                <button
                  className="c-image-upload__cancel button button--alert-subtle pull-left"
                  type="button"
                  onKeyPress={e =>
                    isKeyClick(e) ? this._handleDiscard(e) : null
                  }
                  onClick={this._handleDiscard}
                >
                  {getStyleTag("button--alert-subtle")}
                  <FormattedMessage {...sharedMessages.buttonDiscardChanges} />
                </button>
              )}

              {saving && (
                <div className="mts">
                  <Line
                    percent={uploadPercent}
                    strokeWidth="4"
                    strokeColor="#3289A8"
                  />
                  <p className="h4 text-center">
                    <em>
                      <FormattedMessage
                        {...sharedMessages.formMessagePleaseWait}
                      />
                    </em>
                  </p>
                </div>
              )}

              <button
                className={classnames(
                  "c-image-upload__save button button--can-load pull-right",
                  {
                    "state-loading": this.state.saving
                  }
                )}
                type="submit"
              >
                {resource
                  ? intl.formatMessage(sharedMessages.buttonSaveChanges)
                  : intl.formatMessage(sharedMessages.buttonUploadFile, {
                      type
                    })}
              </button>
            </div>
          )}
        </form>
      </div>
    )
  }

  _onDrop = files => {
    if (files.length === 0) {
      this.setState({
        errors: {
          dropzone: true
        }
      })
      return
    }

    console.log("files", files)

    this.setState({
      files,
      errors: update(this.state.errors, { $merge: { files: null } })
    })

    if (this.props.handleDrop) {
      this.props.handleDrop(files)
    }
  }

  _validate = () => {
    return this._requiredFields.reduce((errors, field) => {
      if (field.name === "files") {
        if (this.state.showFileUpload && this.state.files.length < 1) {
          errors.push(field)
        }
      } else if (field.name === "description") {
        const description = this.state.fields[field.name]
        const value = RichTextEditor.RTEValuetoString(description)

        if (!value) {
          errors.push(field)
        }
      } else if (!this.state.fields[field.name]) {
        errors.push(field)
      }
      return errors
    }, [])
  }

  _handleMediaClear = e => {
    e.preventDefault()

    this.setState({
      files: [],
      errors: {}
    })

    if (this.props.handleDrop) {
      this.props.handleDrop([])
    }
  }

  _handleCancel = e => {
    e.preventDefault()

    this.setState({
      files: [],
      fields: {},
      errors: {}
    })

    if (this.props.handleDrop) {
      this.props.handleDrop([])
    }
  }

  _handleDiscard = e => {
    e.preventDefault()

    browserHistory.push("/contribute")
  }

  _handleSubmit = e => {
    e.preventDefault()

    const errors = this._validate()

    if (errors.length > 0) {
      this.setState({
        errors: keyBy(errors, "name")
      })

      return
    } else {
      this.setState({ errors: {} })
    }

    //return out if we are already saving
    if (this.state.saving) {
      return
    }

    this.setState({
      saved: false,
      saving: true,
      unsaved: false
    })

    //console.log(this.state.files)

    const fileType = this.state.files.length > 0 && this.state.files[0].type

    // if type prop is media then parse type from file info, otherwise just use this.props.type
    const mediaType =
      this.props.type === "media" && fileType
        ? fileType.substr(0, fileType.indexOf("/"))
        : this.props.resource
          ? this.props.resource.type
          : this.props.type

    let description = this.state.fields["description"]

    if (typeof description !== "string") {
      description = description.toString("html")
    }

    const currentDate = moment ? moment().format("YYYY-MM-DD") : null

    const {
      fields: { tags }
    } = this.state
    //Convert tags string into array
    const tagsArr = tags && tags.split(",").map(item => item.trim())

    saveMedia(
      {
        ...this.state.fields,
        description: description,
        id: this.state.id || undefined,
        type: mediaType,
        tags: tagsArr || [],
        contributed_at: currentDate
      },
      this.state.files,
      progress => {
        this.setState({
          uploadPercent: progress.percent
        })
      },
      !!this.props.resource
    )
      .then(res => {
        this.setState({
          id: res.resource_id,
          saved: true,
          saving: false,
          unsaved: false,
          uploadedType: mediaType,
          errors: {}
        })

        if (this.props.handleSaved) {
          this.props.handleSaved(res)
        }
      })
      .catch(err => {
        this.setState({
          saved: false,
          saving: false,
          errors: { form: err.message }
        })

        if (this.props.handleError) {
          this.props.handleError(err)
        }
      })
  }

  _changeValue = e => {
    const field = e.target.getAttribute("name")
    const value = e.target.value

    this._changeField(field, value)
  }

  _changeField(field, value) {
    this.setState({
      fields: update(this.state.fields, { $merge: { [field]: value } })
    })

    const updateParentFields = this.props.updateParentFields

    if (updateParentFields) {
      updateParentFields(this.state.fields)
    }

    if (this.props.handleUnsaved) {
      this.props.handleUnsaved()
    }
  }
}

MediaUpload.propTypes = {
  type: PropTypes.oneOf(["media", "image", "audio", "video"]),
  handleSaved: PropTypes.func,
  handleDrop: PropTypes.func,
  handleError: PropTypes.func,
  handleStatus: PropTypes.func,
  handleUnsaved: PropTypes.func
}

MediaUpload.defaultProps = {
  type: "media",
  showMediaClear: false
}

export default Relay.createContainer(injectIntl(MediaUpload), {
  fragments: {
    resource: () => Relay.QL`
        fragment on Resource {
            id
            title
            creator
            type
            custom_fields
            ${InlineResource.getFragment("resource")}
        }
    `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/MediaUpload.js